/**
 * <variant-listbox> - accessible custom dropdown for high-variant-count products.
 * Used by sections/product-hero-dropdown.liquid only; does not affect the
 * default swatch selector (snippets/swatch.liquid) used everywhere else.
 *
 * No option is preselected on load - the dropdown shows the option-name
 * placeholder and the CTA stays disabled ("Select a colour") until a shopper
 * actually chooses one. If a variant image gallery was rendered (see
 * sections/product-hero-dropdown.liquid), it opens on its leading
 * placeholder/primary-image slide (Swiper's default initial index) and is
 * kept in sync two-ways with the dropdown once a selection is made (see
 * initGallery()). Selecting an option updates: the hidden native <select>
 * (for /cart/add form submission), the hero image or gallery, and the CTA
 * button's label/price/enabled state, all read from data-* attributes
 * rendered server-side per variant (see snippets/variant-listbox.liquid).
 */
if (!customElements.get('variant-listbox')) {
  class VariantListbox extends HTMLElement {
    constructor() {
      super();
      this.trigger = this.querySelector('[data-listbox-trigger]');
      this.triggerLabel = this.querySelector('[data-listbox-label]');
      this.triggerThumb = this.querySelector('[data-listbox-thumb]');
      this.popup = this.querySelector('[data-listbox-popup]');
      this.options = Array.from(this.querySelectorAll('[role="option"]'));
      this.hiddenSelect = this.querySelector('[data-variant-select]');

      const form = this.closest('form');
      this.heroImage = document.querySelector('[data-variant-hero-image]');
      this.gallery = document.querySelector('[data-variant-gallery]');
      this.gallerySwiper = null;
      this.gallerySlides = [];
      this.ctaButton = form ? form.querySelector('[data-cta-button]') : null;
      this.ctaLabel = form ? form.querySelector('[data-cta-label]') : null;
      this.ctaPrice = form ? form.querySelector('[data-cta-price]') : null;
      this.ctaComparePrice = form ? form.querySelector('[data-cta-compare-price]') : null;
      this.ctaSalePrice = form ? form.querySelector('[data-cta-sale-price]') : null;

      this.isMember = this.dataset.isMember === 'true';
      this.addToCartLabel = this.dataset.addToCartLabel;
      this.soldOutLabel = this.dataset.soldOutLabel;

      // Even when every variant is sold out, the listbox stays open-able so
      // shoppers can still browse colorways - the CTA button (rendered
      // disabled "Sold Out" server-side when product.available is false) is
      // the single source of truth for purchasability, not the trigger.
      this.trigger.addEventListener('click', this.toggle.bind(this));
      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(option));
      });
      this.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.close();
      });

      this.initGallery();
    }

    // Wires up the Swiper gallery (rendered only when at least one variant
    // has its own image - see sections/product-hero-dropdown.liquid) and
    // syncs it two-ways with variant selection: scrolling to a slide selects
    // its variant, and selecting a variant scrolls the gallery to match (see
    // the gallerySwiper.slideTo call at the end of select()).
    initGallery() {
      if (!this.gallery) return;

      this.gallerySlides = Array.from(this.gallery.querySelectorAll('.swiper-slide'));

      if (this.gallerySlides.length > 1) {
        this.gallerySwiper = new Swiper(this.gallery, {
          direction: 'horizontal',
          loop: false,
          slidesPerView: 1,
          navigation: {
            nextEl: '.swiper-button-next-pdp',
            prevEl: '.swiper-button-prev-pdp',
          },
        });
        this.gallerySwiper.on('slideChange', () => {
          const slide = this.gallerySlides[this.gallerySwiper.activeIndex];
          const ids = this.slideVariantIds(slide);
          if (!ids.length) return;
          // Keep the current variant if it already owns this image.
          const current = this.options.find((el) => el.getAttribute('aria-selected') === 'true');
          if (current && ids.includes(current.dataset.variantId)) return;
          const option = this.options.find((el) => ids.includes(el.dataset.variantId));
          if (option) this.select(option, { focusTrigger: false });
        });
      } else {
        this.gallery.classList.add('single-slide');
      }

      // No auto-select here: Swiper's own default initial index (0) is
      // gallerySlides[0], the leading placeholder/primary-image slide (see
      // sections/product-hero-dropdown.liquid) - so it already opens on the
      // right image with nothing selected. A shopper's first real pick
      // (dropdown click, or scrolling to a variant slide) drives select().
    }

    slideVariantIds(slide) {
      return (slide.dataset.variantIds || '').split(' ').filter(Boolean);
    }

    isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.trigger.setAttribute('aria-expanded', 'true');
      this.popup.hidden = false;
      const selected = this.options.find((option) => option.getAttribute('aria-selected') === 'true');
      const firstEnabled = this.options.find((option) => option.getAttribute('aria-disabled') !== 'true');
      const toFocus = selected || firstEnabled;
      if (toFocus) toFocus.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen()) return;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.popup.hidden = true;
    }

    enabledOptions() {
      return this.options.filter((option) => option.getAttribute('aria-disabled') !== 'true');
    }

    onKeydown(event) {
      if (!this.isOpen()) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
        return;
      }

      const enabled = this.enabledOptions();
      const activeIndex = enabled.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          enabled[Math.min(activeIndex + 1, enabled.length - 1)]?.focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          enabled[Math.max(activeIndex - 1, 0)]?.focus();
          break;
        case 'Home':
          event.preventDefault();
          enabled[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          enabled[enabled.length - 1]?.focus();
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.options.includes(document.activeElement)) {
            this.select(document.activeElement);
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.close();
          this.trigger.focus({ preventScroll: true });
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    // focusTrigger is false when select() is driven by something other than
    // a direct interaction with the trigger/options (gallery slideChange) -
    // that shouldn't steal keyboard focus onto the trigger button the way an
    // explicit click/keydown selection should.
    select(option, { focusTrigger = true } = {}) {
      if (option.getAttribute('aria-disabled') === 'true') return;

      this.options.forEach((el) => el.setAttribute('aria-selected', 'false'));
      option.setAttribute('aria-selected', 'true');

      const variantId = option.dataset.variantId;
      const available = option.dataset.available === 'true';
      const image = option.dataset.image;
      const thumb = option.dataset.thumb;

      // Members: the regular variant price is struck through against the
      // member price. Non-members: Shopify's native compare-at price (if
      // any) is struck through against the variant price - same pairing
      // the default swatch selector shows.
      let comparePrice = '';
      let salePrice = option.dataset.price;
      if (this.isMember) {
        comparePrice = option.dataset.price;
        salePrice = option.dataset.memberPrice;
      } else if (option.dataset.compareAtPrice) {
        comparePrice = option.dataset.compareAtPrice;
      }

      this.triggerLabel.textContent = option.dataset.label;

      if (this.triggerThumb) {
        if (thumb) {
          this.triggerThumb.src = thumb;
          this.triggerThumb.hidden = false;
        } else {
          this.triggerThumb.removeAttribute('src');
          this.triggerThumb.hidden = true;
        }
      }

      if (this.hiddenSelect) {
        this.hiddenSelect.value = variantId;
        this.hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.heroImage && image) {
        this.heroImage.src = image;
      }

      if (this.gallerySwiper) {
        const targetIndex = this.gallerySlides.findIndex((el) => this.slideVariantIds(el).includes(variantId));
        const activeSlide = this.gallerySlides[this.gallerySwiper.activeIndex];
        const alreadyOnVariantImage = activeSlide && this.slideVariantIds(activeSlide).includes(variantId);
        if (targetIndex !== -1 && !alreadyOnVariantImage) {
          // runCallbacks: false - this slideTo is select() syncing the gallery
          // to match a dropdown-driven selection. Without this it re-fires
          // the slideChange listener below, which calls select() again with
          // the same option (redundant work, and a duplicate
          // EightThirtyStories:variantListbox:change dispatch).
          this.gallerySwiper.slideTo(targetIndex, undefined, false);
        }
      }

      if (this.ctaButton) {
        this.ctaButton.disabled = !available;
        this.ctaButton.classList.toggle('disabled', !available);
      }
      if (this.ctaLabel) {
        this.ctaLabel.textContent = available ? this.addToCartLabel : this.soldOutLabel;
      }
      if (this.ctaPrice) {
        this.ctaPrice.hidden = !available;
      }
      if (this.ctaComparePrice) {
        this.ctaComparePrice.textContent = comparePrice;
        this.ctaComparePrice.hidden = !comparePrice;
      }
      if (this.ctaSalePrice) {
        this.ctaSalePrice.textContent = salePrice || '';
      }

      document.dispatchEvent(new CustomEvent('EightThirtyStories:variantListbox:change', {
        detail: { variantId: variantId, available: available },
      }));

      this.close();
      if (focusTrigger) {
        this.trigger.focus({ preventScroll: true });
      }

      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      }
    }
  }

  customElements.define('variant-listbox', VariantListbox);
}
